<template>
  <div class="card">
    <div class="card-header">
      <div class="card-header-title">
        <div class="title is-6 is-marginless">
          Κάρτες λίστας <span class="tag">Προσθήκη</span>
        </div>
      </div>

      <div class="card-header-icon">
        <RouterLink
          :to="{
            name: 'content.home.card-link-lists.items',
            params: { id: listId },
          }"
          class="button"
        >
          <span class="icon is-small"><i class="fa fa-list"/></span>
          <span>Λίστα</span>
        </RouterLink>
      </div>
    </div>

    <form @submit.prevent="submit">
      <div class="card-content">
        <!-- Image (WEBP) uploader (same logic as provided snippet) -->
        <AddPhotos
          :title="'Εικόνα/Animation (WEBP)'"
          :preview="preview"
          :show-error="errors.has('image')"
          rule="image/webp"
          @addPhotos="handleAddPhotos"
        />

        <div class="columns is-multiline">
          <!-- Title EL -->
          <div class="column is-half">
            <div class="field">
              <label class="label">Τίτλος (EL) *</label>
              <div class="control">
                <input
                  v-validate="'required'"
                  v-model.trim="form.title_el"
                  class="input"
                  type="text"
                  name="title_el"
                />
                <p v-show="errors.has('title_el')" class="help is-danger">
                  Εισάγετε τίτλο στα Ελληνικά
                </p>
              </div>
            </div>
          </div>

          <!-- Title EN -->
          <div class="column is-half">
            <div class="field">
              <label class="label">Τίτλος (EN) *</label>
              <div class="control">
                <input
                  v-validate="'required'"
                  v-model.trim="form.title_en"
                  class="input"
                  type="text"
                  name="title_en"
                />
                <p v-show="errors.has('title_en')" class="help is-danger">
                  Εισάγετε τίτλο στα Αγγλικά
                </p>
              </div>
            </div>
          </div>

          <!-- Link -->
          <div class="column is-full">
            <div class="field">
              <label class="label">Link (URL) *</label>
              <BookshelfLinkNotice />
              <div class="control">
                <input
                  v-validate="'required|url'"
                  v-model.trim="form.link"
                  class="input"
                  type="text"
                  name="link"
                  placeholder="https://..."
                />
                <p v-show="errors.has('link')" class="help is-danger">
                  Εισάγετε έγκυρο link (URL)
                </p>
              </div>
            </div>
          </div>

          <!-- Initial order (optional) -->
          <div class="column is-one-third">
            <div class="field">
              <label class="label">Σειρά εμφάνισης (προαιρετικό)</label>
              <div class="control">
                <input
                  v-model.number="form.order"
                  class="input"
                  type="number"
                  min="0"
                  step="1"
                  name="order"
                />
              </div>
              <p class="help">Αλλιώς ορίστε μετά από τη λίστα με «Σύρετε».</p>
            </div>
          </div>

          <!-- Active -->
          <div class="column is-full">
            <div class="field">
              <label class="label" title="Μέγιστο 4 ενεργές κάρτες">
                Εμφανές *
              </label>
              <div class="control">
                <ToggleButton
                  :sync="true"
                  :value="form.is_active"
                  :color="{ checked: '#22A684', unchecked: '#ff3860' }"
                  :width="30"
                  :height="15"
                  @change="updateActive"
                />
              </div>
              <p class="help">Μέγιστο 4 ενεργές κάρτες επιτρέπονται.</p>
            </div>
          </div>
        </div>
      </div>

      <div class="card-footer">
        <FormSubmit :is-saving="isSaving" @reset="reset" />
      </div>
    </form>
  </div>
</template>

<script>
import BookshelfLinkNotice from '@/views/components/BookshelfLinkNotice';
import request from '@/utils/request';
import FormSubmit from '@/views/components/FormSubmit';
import AddPhotos from '@/views/components/AddPhotos';

export default {
  name: 'CardLinkItemsAdd',
  components: {
    BookshelfLinkNotice,
    FormSubmit,
    AddPhotos,
  },

  props: {
    id: { type: [String, Number], required: true }, // list id from route
  },

  data() {
    return {
      form: {
        title_el: '',
        title_en: '',
        link: '',
        is_active: false,
        order: 0,
        image: null,
      },
      preview: '',
      isSaving: false,
    };
  },

  computed: {
    listId() {
      return Number(this.id);
    },
  },

  methods: {
    updateActive({ value }) {
      this.form.is_active = !!value;
    },

    // keep EXACT logic as the provided snippet (FileReader + preview + vee-validate hooks)
    handleAddPhotos(image) {
      this.form.image = image;

      const reader = new FileReader();
      this.$validator
        .validateAll()
        .then(() => {
          reader.onload = e => {
            this.preview = e.target.result;
            this.errors.remove('image');
          };
          reader.readAsDataURL(image);
        })
        .catch(err => console.log(err));
    },

    async submit() {
      try {
        const ok = await this.$validator.validateAll();

        if (!this.preview) {
          this.errors.add({ field: 'image', msg: 'Απαιτείται εικόνα' });
        }

        if (!ok || !this.preview) {
          throw new Error('Client-side validation failed');
        }

        if (!this.form.image) {
          this.errors.add({ field: 'image', msg: 'Απαιτείται εικόνα' });
          throw new Error('Client-side validation failed');
        }

        this.isSaving = true;

        const fd = new FormData();
        fd.append('title_el', this.form.title_el);
        fd.append('title_en', this.form.title_en);
        fd.append('link', this.form.link);
        fd.append('is_active', this.form.is_active ? '1' : '0');
        fd.append('order', String(this.form.order || 0));
        fd.append('image', this.form.image);

        const {
          data,
          status,
        } = await request.post(
          `/home/card-link-lists/${this.listId}/items`,
          fd,
          { headers: { 'Content-Type': 'multipart/form-data' } },
        );

        this.isSaving = false;

        if (status !== 201) {
          throw new Error(data?.message || 'To αίτημα απέτυχε');
        }

        this.$router
          .push({
            name: 'content.home.card-link-lists.items',
            params: { id: this.listId },
          })
          .catch(() => {});
        this.$notify({
          type: 'success',
          title: 'Επιτυχία',
          text: 'Η κάρτα αποθηκεύτηκε',
        });
      } catch (err) {
        this.isSaving = false;
        const msg =
          err?.response?.data?.message || err?.message || 'To αίτημα απέτυχε';
        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text: msg.includes('up to 4') ? 'Μέγιστο 4 ενεργές κάρτες' : msg,
        });
      }
    },

    reset() {
      this.form = {
        title_el: '',
        title_en: '',
        link: '',
        is_active: false,
        order: 0,
        image: null,
      };
      this.preview = '';
      this.$validator.reset();
      this.errors.remove('image');
    },
  },
};
</script>
